/**
 * Verification status badge component
 *
 * Displays the trust level of data based on source quality:
 * - VERIFIED: Authoritative source (SEC, company website)
 * - CORROBORATED: Multiple independent sources agree
 * - UNVERIFIED: Single non-authoritative source
 * - STALE: Previously verified but may be outdated
 */

import React from 'react';

export type VerificationStatus = 'verified' | 'corroborated' | 'unverified' | 'stale';

interface VerificationBadgeProps {
  status: VerificationStatus;
  showLabel?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

const statusConfig: Record<VerificationStatus, {
  label: string;
  bg: string;
  text: string;
  icon: React.ReactNode;
  title: string;
}> = {
  verified: {
    label: 'Verified',
    bg: 'bg-green-100',
    text: 'text-green-800',
    icon: (
      <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
      </svg>
    ),
    title: 'Verified from authoritative source (SEC filing, company website)',
  },
  corroborated: {
    label: 'Corroborated',
    bg: 'bg-blue-100',
    text: 'text-blue-800',
    icon: (
      <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
      </svg>
    ),
    title: 'Confirmed by 2+ independent sources',
  },
  unverified: {
    label: 'Unverified',
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
    icon: (
      <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
      </svg>
    ),
    title: 'Single source, awaiting verification',
  },
  stale: {
    label: 'Stale',
    bg: 'bg-gray-100',
    text: 'text-gray-600',
    icon: (
      <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
      </svg>
    ),
    title: 'Data may be outdated, needs refresh',
  },
};

export function VerificationBadge({
  status,
  showLabel = true,
  size = 'sm',
  className = '',
}: VerificationBadgeProps) {
  const config = statusConfig[status];

  const sizeClasses = size === 'sm'
    ? 'px-1.5 py-0.5 text-xs'
    : 'px-2 py-1 text-sm';

  return (
    <span
      className={`inline-flex items-center gap-1 rounded font-medium ${config.bg} ${config.text} ${sizeClasses} ${className}`}
      title={config.title}
    >
      {config.icon}
      {showLabel && <span>{config.label}</span>}
    </span>
  );
}

/**
 * Compact verification indicator (just the icon)
 */
export function VerificationIndicator({
  status,
  className = '',
}: {
  status: VerificationStatus;
  className?: string;
}) {
  const config = statusConfig[status];

  // Only show indicator for verified/corroborated
  if (status === 'unverified') return null;

  const bgColor = status === 'verified'
    ? 'bg-green-500'
    : status === 'corroborated'
    ? 'bg-blue-500'
    : 'bg-gray-400';

  return (
    <span
      className={`inline-flex h-4 w-4 items-center justify-center rounded-full ${bgColor} text-white ${className}`}
      title={config.title}
    >
      {status === 'verified' && (
        <svg className="h-2.5 w-2.5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      )}
      {status === 'corroborated' && (
        <svg className="h-2.5 w-2.5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
      )}
    </span>
  );
}

/**
 * Value display with range indicator for estimated data
 */
interface ValueRangeDisplayProps {
  valueLow?: number;
  valueMid: number;
  valueHigh?: number;
  unit?: string;
  isEstimated?: boolean;
  className?: string;
}

export function ValueRangeDisplay({
  valueLow,
  valueMid,
  valueHigh,
  unit,
  isEstimated = false,
  className = '',
}: ValueRangeDisplayProps) {
  const formatNum = (val: number): string => {
    if (unit === 'USD') {
      if (val >= 1e12) return `$${(val / 1e12).toFixed(1)}T`;
      if (val >= 1e9) return `$${(val / 1e9).toFixed(1)}B`;
      if (val >= 1e6) return `$${(val / 1e6).toFixed(1)}M`;
      return `$${val.toLocaleString()}`;
    }
    if (val >= 1e6) return `${(val / 1e6).toFixed(1)}M`;
    if (val >= 1e3) return `${(val / 1e3).toFixed(1)}K`;
    return val.toLocaleString();
  };

  // Show range for low-confidence estimates
  if (isEstimated && valueLow && valueHigh) {
    return (
      <span className={`inline-flex items-baseline gap-1 ${className}`}>
        <span className="text-gray-600">{formatNum(valueLow)}</span>
        <span className="text-gray-400">–</span>
        <span className="text-gray-600">{formatNum(valueHigh)}</span>
        <span className="ml-1 text-xs text-yellow-600">(Est.)</span>
      </span>
    );
  }

  return (
    <span className={className}>
      {formatNum(valueMid)}
      {isEstimated && <span className="ml-1 text-xs text-yellow-600">(Est.)</span>}
    </span>
  );
}

export default VerificationBadge;
